import { PencilEditIcon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { LONG_DASH, NOT_APPLICABLE } from '@dealstack/frontend/shared/other/react/specialSigns';
import { useSecurityTypeTranslation } from '@dealstack/frontend/shared/translationControllers/useCommonTranslationsController';
import { SecurityType } from '@dealstack/shared/lib/generated';
import { Text } from '@mantine/core';
import { get } from 'lodash';
import { memo, ReactElement, useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { SecuritiesFormValues } from '@dealstack/frontend/shared/components/SecuritiesTable/useSecuritiesTableController';
import { FormSingleSecurity } from '@dealstack/frontend/shared/components/SecuritiesTable/useSecuritiesTableController.types';

interface SecurityRowProps {
	className?: string;
	fieldIndex: number;
	onRemove: () => void;
	showNumericalColumn?: boolean;
	resetEditOnDestroy?: boolean;
	showFairMarketValuePerUnitColumn?: boolean;
	showEditButton?: boolean;
	onEditSecurity?: (security: FormSingleSecurity) => void;
}

const SecurityRow = ({
	fieldIndex,
	onRemove,
	className,
	resetEditOnDestroy,
	showEditButton,
	onEditSecurity,
}: SecurityRowProps): ReactElement => {
	const getSecurityTypeTranslation = useSecurityTypeTranslation();
	const tMipManage = useTranslationMipManage();
	const securitiesForm = useFormContext();

	const fieldKeys = {
		field: `${SecuritiesFormValues.securities}.${fieldIndex}` as `${SecuritiesFormValues.securities}.${number}`,
		isOnEdit:
			`${SecuritiesFormValues.securities}.${fieldIndex}.isOnEdit` as `${SecuritiesFormValues.securities}.${number}.isOnEdit`,
	};

	const fieldData: FormSingleSecurity = securitiesForm.watch(fieldKeys.field);

	const stopEdit = useCallback(() => {
		const securitiesFields = get(securitiesForm.getValues(), SecuritiesFormValues.securities);
		const index = securitiesFields.findIndex((item) => item.id === fieldData.id);
		const field = securitiesFields[index];
		if (!field) {
			return;
		}
		securitiesForm.setValue(fieldKeys.isOnEdit, false);
	}, [securitiesForm, fieldKeys.isOnEdit, fieldData.id]);

	// The drawer owns the edit lifecycle, so the row must not flag itself as on-edit:
	// dismissing the drawer without saving would leave the flag set, which blocks Continue.
	const onEditButtonClicked = () => {
		if (onEditSecurity) {
			onEditSecurity(securitiesForm.getValues(fieldKeys.field));
			return;
		}
		securitiesForm.setValue(fieldKeys.isOnEdit, true);
	};

	const loansSecurityTypes = new Set([
		SecurityType.LOAN,
		SecurityType.OPTIONS,
		SecurityType.OTHER,
		SecurityType.WARRANT,
	]);

	const disableForLoansSecurityTypes = loansSecurityTypes.has(fieldData.type);

	const getVotesPerUnitValue = () => {
		if (fieldData.type === SecurityType.PARTNERSHIP_UNIT || disableForLoansSecurityTypes) {
			return NOT_APPLICABLE;
		}
		if (fieldData.votesPerUnit !== undefined && fieldData.votesPerUnit !== null) {
			return fieldData.votesPerUnit;
		}
		return LONG_DASH;
	};

	const getNominalValue = () => {
		if (disableForLoansSecurityTypes) {
			return NOT_APPLICABLE;
		}
		const value = Number(fieldData.nominalValue);
		if (!isNaN(value) && isFinite(value)) {
			return value.toFixed(5);
		}
		return LONG_DASH;
	};

	useEffect(() => {
		return () => {
			if (!(resetEditOnDestroy && fieldData.isOnEdit)) {
				return;
			}

			stopEdit();
			if (fieldData.isOnAdd) {
				onRemove();
			}
		};
	}, [resetEditOnDestroy, fieldData.isOnEdit, fieldData.isOnAdd, stopEdit, onRemove]);

	return (
		<tr className={`security-row ${className || ''}`}>
			<TableCell>{getSecurityTypeTranslation(fieldData.type)}</TableCell>
			<TableCell>{fieldData.name || '-'}</TableCell>
			<TableCell align="center">{getVotesPerUnitValue()}</TableCell>
			<TableCell align="center">{getNominalValue()}</TableCell>
			<TableCell align="center">
				<Text>
					{fieldData.isSubjectToVesting
						? tMipManage('root.securities.table.values.yes')
						: tMipManage('root.securities.table.values.no')}
				</Text>
			</TableCell>
			<TableCell align="center">
				{!fieldData.locked && showEditButton && (
					<EditButton
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();
							onEditButtonClicked();
						}}
						aria-label={`Edit ${fieldData.name || 'security'}`}
					>
						<PencilEditIcon />
					</EditButton>
				)}
			</TableCell>
		</tr>
	);
};

const EditButton = styled.button`
	background: none;
	border: none;
	cursor: pointer;
	color: ${({ theme }) => theme.v3.colors.blue[6]};
	padding: 0.25rem;
	display: flex;
	align-items: center;
	justify-content: center;

	&:hover {
		background-color: ${({ theme }) => theme.v3.colors.blue[0]};
		border-radius: 4px;
	}
`;

interface TableCellProps {
	align?: 'center' | 'left' | 'right';
}

const TableCell = styled.td<TableCellProps>`
	padding: 12px 16px;
	text-align: ${(props) => props.align || 'left'};
`;
const _SecurityRowExport = memo(SecurityRow);

export { _SecurityRowExport as SecurityRow };
